import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faEye, faEyeSlash, faShieldAlt, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

const PasswordSettings: React.FC = () => {
  // @ts-ignore - react-i18next supports namespace parameter but TypeScript types don't reflect it
  const { t } = useTranslation('settings');
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field]
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      alert(t('passwordSettings.passwordMismatchAlert'));
      return;
    }
    
    if (formData.newPassword.length < 8) {
      alert(t('passwordSettings.passwordTooShortAlert'));
      return;
    }
    
    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      console.log('Password updated');
    }, 2000);
  };

  const passwordRequirements = [
    { text: t('passwordSettings.requirementMin8'), met: formData.newPassword.length >= 8 },
    { text: t('passwordSettings.requirementUpperCase'), met: /[A-Z]/.test(formData.newPassword) },
    { text: t('passwordSettings.requirementLowerCase'), met: /[a-z]/.test(formData.newPassword) },
    { text: t('passwordSettings.requirementNumber'), met: /\d/.test(formData.newPassword) },
    { text: t('passwordSettings.requirementSpecial'), met: /[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword) }
  ];

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">{t('passwordSettings.title')}</h2>
        <p className="text-white/70">{t('passwordSettings.description')}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Current Password */}
        <div>
          <label htmlFor="currentPassword" className="block text-sm font-medium text-white/80 mb-2">
            {t('passwordSettings.currentPassword')}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FontAwesomeIcon icon={faLock} className="text-white/40 text-sm" />
            </div>
            <input
              type={showPasswords.current ? 'text' : 'password'}
              id="currentPassword"
              name="currentPassword"
              value={formData.currentPassword}
              onChange={handleInputChange}
              className="w-full pl-10 pr-12 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
              placeholder="••••••••"
              required
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('current')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-white/40 hover:text-white/60 transition-colors"
            >
              <FontAwesomeIcon icon={showPasswords.current ? faEyeSlash : faEye} className="text-sm" />
            </button>
          </div>
        </div>

        {/* New Password */}
        <div>
          <label htmlFor="newPassword" className="block text-sm font-medium text-white/80 mb-2">
            {t('passwordSettings.newPassword')}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FontAwesomeIcon icon={faLock} className="text-white/40 text-sm" />
            </div>
            <input
              type={showPasswords.new ? 'text' : 'password'}
              id="newPassword"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleInputChange}
              className="w-full pl-10 pr-12 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
              placeholder="••••••••"
              required
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('new')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-white/40 hover:text-white/60 transition-colors"
            >
              <FontAwesomeIcon icon={showPasswords.new ? faEyeSlash : faEye} className="text-sm" />
            </button>
          </div>
        </div>

        {/* Password Requirements */}
        {formData.newPassword && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
            <h4 className="text-sm font-medium text-white mb-3">{t('passwordSettings.passwordRequirements')}</h4>
            <div className="space-y-2">
              {passwordRequirements.map((requirement, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <FontAwesomeIcon 
                    icon={requirement.met ? faCheck : faTimes} 
                    className={`text-sm ${requirement.met ? 'text-green-400' : 'text-red-400'}`} 
                  />
                  <span className={`text-sm ${requirement.met ? 'text-green-300' : 'text-red-300'}`}>
                    {requirement.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Confirm Password */}
        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-white/80 mb-2">
            {t('passwordSettings.confirmPassword')}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FontAwesomeIcon icon={faLock} className="text-white/40 text-sm" />
            </div>
            <input
              type={showPasswords.confirm ? 'text' : 'password'}
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className="w-full pl-10 pr-12 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
              placeholder="••••••••"
              required
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('confirm')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-white/40 hover:text-white/60 transition-colors"
            >
              <FontAwesomeIcon icon={showPasswords.confirm ? faEyeSlash : faEye} className="text-sm" />
            </button>
          </div>
          {formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
            <p className="mt-1 text-xs text-red-400">{t('passwordSettings.passwordsNotMatch')}</p>
          )}
        </div>

        {/* Security Tips */}
        <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-400/20 rounded-xl p-6">
          <div className="flex items-start space-x-3">
            <FontAwesomeIcon icon={faShieldAlt} className="text-blue-400 text-lg mt-1" />
            <div>
              <h4 className="text-lg font-semibold text-white mb-2">{t('passwordSettings.securityTips')}</h4>
              <ul className="space-y-2 text-white/70 text-sm">
                <li>• {t('passwordSettings.securityTip1')}</li>
                <li>• {t('passwordSettings.securityTip2')}</li>
                <li>• {t('passwordSettings.securityTip3')}</li>
                <li>• {t('passwordSettings.securityTip4')}</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-start md:justify-end pt-6 border-t border-white/10">
          <button
            type="submit"
            disabled={isLoading || !passwordRequirements.every(req => req.met) || formData.newPassword !== formData.confirmPassword}
            className="flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-purple-700 px-8 py-3 rounded-xl text-white font-semibold hover:from-purple-600 hover:to-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-transparent transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>{t('passwordSettings.updating')}</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faLock} className="text-sm" />
                <span>{t('passwordSettings.updatePassword')}</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PasswordSettings;